import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BarChart2 } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { ProfitabilityTabs } from "@/components/ProfitabilityTabs";
import { calcNetProfit, useApp, type SaleVenue } from "@/context/AppContext";
import { computeBundlePerformance } from "@/features/bundlePerformance";
import {
  aggregateByProductType,
  computeProductTimings,
  deriveThresholds,
  findSlowInventory,
  SLOW_INVENTORY_DEFAULT_DAYS,
  type ProductTypeAggregate,
  type SpeedBucket,
} from "@/features/sellThrough";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/analytics")({
  component: () => (
    <ManagerOnly>
      <AnalyticsPage />
    </ManagerOnly>
  ),
});

type Period = "all" | "quarter" | "month" | "week";

const PERIODS: Period[] = ["week", "month", "quarter", "all"];
const PERIOD_LABELS: Record<Period, string> = {
  week: "7 Days",
  month: "30 Days",
  quarter: "90 Days",
  all: "All Time",
};

const VENUE_COLORS: Record<SaleVenue, string> = {
  eBay: "#e8b923",
  "Facebook Marketplace": "#4267B2",
  Local: "#27ae60",
  TCGplayer: "#e74c3c",
  Website: "#9b59b6",
  Other: "#8e8e93",
};

const SPEED_PILL: Record<SpeedBucket, string> = {
  Fast: "bg-emerald-500/20 text-emerald-500",
  Medium: "bg-secondary/20 text-secondary",
  Slow: "bg-rose-500/20 text-rose-500",
};

function AnalyticsPage() {
  const { sales, lots, products, liveListings } = useApp();
  const [period, setPeriod] = useState<Period>("all");

  const filteredSales = useMemo(() => {
    if (period === "all") return sales;
    const days = period === "quarter" ? 90 : period === "month" ? 30 : 7;
    const cutoff = Date.now() - days * 86400000;
    return sales.filter((s) => new Date(s.saleDate).getTime() >= cutoff);
  }, [sales, period]);

  const stats = useMemo(() => {
    const totalRevenue = filteredSales.reduce(
      (acc, s) => acc + s.salePrice + s.buyerShipping,
      0,
    );
    const totalNet = filteredSales.reduce(
      (acc, s) => acc + calcNetProfit(s),
      0,
    );
    const totalFees = filteredSales.reduce(
      (acc, s) => acc + s.platformFees + s.paymentFees,
      0,
    );
    const totalShipping = filteredSales.reduce(
      (acc, s) => acc + s.actualShipping,
      0,
    );
    const totalInvested = lots.reduce(
      (acc, l) => acc + l.purchaseCost + l.shippingCost + l.taxFees,
      0,
    );
    const roi = totalInvested > 0 ? (totalNet / totalInvested) * 100 : 0;
    const margin = totalRevenue > 0 ? (totalNet / totalRevenue) * 100 : 0;
    const avgSale =
      filteredSales.length > 0 ? totalRevenue / filteredSales.length : 0;
    return {
      totalRevenue,
      totalNet,
      totalFees,
      totalShipping,
      totalInvested,
      roi,
      margin,
      avgSale,
    };
  }, [filteredSales, lots]);

  const byVenue = useMemo(() => {
    const map: Record<string, { profit: number; count: number }> = {};
    for (const s of filteredSales) {
      if (!map[s.venue]) map[s.venue] = { profit: 0, count: 0 };
      map[s.venue].profit += calcNetProfit(s);
      map[s.venue].count += 1;
    }
    return Object.entries(map).sort((a, b) => b[1].profit - a[1].profit);
  }, [filteredSales]);

  const byLot = useMemo(() => {
    const lotMap: Record<
      string,
      {
        name: string;
        invested: number;
        revenue: number;
        net: number;
        sales: number;
      }
    > = {};
    for (const l of lots) {
      lotMap[l.id] = {
        name: l.seller || `Lot ${l.id.slice(-4)}`,
        invested: l.purchaseCost + l.shippingCost + l.taxFees,
        revenue: 0,
        net: 0,
        sales: 0,
      };
    }
    for (const s of filteredSales) {
      if (s.sourceLotId && lotMap[s.sourceLotId]) {
        const n = calcNetProfit(s);
        lotMap[s.sourceLotId].revenue += s.salePrice + s.buyerShipping;
        lotMap[s.sourceLotId].net += n;
        lotMap[s.sourceLotId].sales += 1;
      }
    }
    return Object.values(lotMap)
      .filter((l) => l.sales > 0)
      .sort((a, b) => b.net - a.net);
  }, [lots, filteredSales]);

  const maxVenueProfit = byVenue.length > 0 ? byVenue[0][1].profit : 1;
  const maxLotNet = byLot.length > 0 ? Math.max(byLot[0].net, 1) : 1;

  const bundlePerformance = useMemo(
    () =>
      computeBundlePerformance(products, filteredSales).filter(
        (s) => s.productCount > 0,
      ),
    [products, filteredSales],
  );

  const [slowThresholdDays, setSlowThresholdDays] = useState<number>(
    SLOW_INVENTORY_DEFAULT_DAYS,
  );

  const sellThrough = useMemo(() => {
    // Slow inventory is period-independent by definition: short periods can't
    // contain listings older than a long threshold. Period only filters the
    // sales-based fastest/slowest rankings.
    const allTimings = computeProductTimings(products, liveListings, sales);
    const slow = findSlowInventory(allTimings, slowThresholdDays).slice(0, 5);

    const periodTimings = computeProductTimings(
      products,
      liveListings,
      filteredSales,
    );
    const periodSoldDays = periodTimings
      .filter((t) => t.daysToSell != null)
      .map((t) => t.daysToSell!);
    const thresholds = deriveThresholds(periodSoldDays);
    const ranked = aggregateByProductType(periodTimings, thresholds, 2);
    const fastest = ranked.slice(0, 3);
    const fastestKeys = new Set<string>(fastest.map((r) => r.productType));
    const slowest: ProductTypeAggregate[] = [...ranked]
      .reverse()
      .filter((r) => !fastestKeys.has(r.productType))
      .slice(0, 3);
    return { fastest, slowest, slow };
  }, [products, liveListings, sales, filteredSales, slowThresholdDays]);

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Profitability</h1>
        <p className="text-sm text-muted-foreground">
          Analytics — business performance from {filteredSales.length} sale
          {filteredSales.length !== 1 ? "s" : ""}.
        </p>
      </div>

      <ProfitabilityTabs current="analytics" />

      <div
        className="flex-1 space-y-4 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {/* Period toggle */}
        <div className="flex gap-1 rounded-md bg-card p-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`flex-1 rounded-md py-2 text-center text-xs font-semibold ${
                period === p
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>

        {/* Sell-through */}
        {(sellThrough.fastest.length > 0 ||
          sellThrough.slowest.length > 0 ||
          sellThrough.slow.length > 0) && (
          <Section title="Sell-Through">
            {sellThrough.fastest.length > 0 && (
              <SellSubsection title="Fastest Selling Product Types">
                {sellThrough.fastest.map((agg) => (
                  <SellRow
                    key={`fast-${agg.productType}`}
                    title={agg.productType}
                    sub={`avg ${agg.avgDaysToSell.toFixed(1)}d to sell · median ${agg.medianDaysToSell.toFixed(1)}d${agg.avgDaysToList != null ? ` · ${agg.avgDaysToList.toFixed(1)}d to list` : ""} · ${agg.saleCount} sale${agg.saleCount !== 1 ? "s" : ""}`}
                    pillText={agg.speed}
                    pillClass={SPEED_PILL[agg.speed]}
                  />
                ))}
              </SellSubsection>
            )}

            {sellThrough.slowest.length > 0 && (
              <SellSubsection title="Slowest Selling Product Types">
                {sellThrough.slowest.map((agg) => (
                  <SellRow
                    key={`slow-${agg.productType}`}
                    title={agg.productType}
                    sub={`avg ${agg.avgDaysToSell.toFixed(1)}d to sell · median ${agg.medianDaysToSell.toFixed(1)}d${agg.avgDaysToList != null ? ` · ${agg.avgDaysToList.toFixed(1)}d to list` : ""} · ${agg.saleCount} sale${agg.saleCount !== 1 ? "s" : ""}`}
                    pillText={agg.speed}
                    pillClass={SPEED_PILL[agg.speed]}
                  />
                ))}
              </SellSubsection>
            )}

            <SellSubsection
              title={`Slow Inventory (>${slowThresholdDays} days, no sale)`}
            >
              <div className="flex flex-wrap gap-1.5">
                {[14, 30, 60, 90].map((d) => {
                  const active = d === slowThresholdDays;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSlowThresholdDays(d)}
                      className={`rounded-md border px-2.5 py-1 text-[11px] font-semibold ${
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {d}d
                    </button>
                  );
                })}
              </div>
              {sellThrough.slow.length === 0 ? (
                <div className="pt-2 text-xs text-muted-foreground">
                  Nothing has been sitting that long. Nice.
                </div>
              ) : (
                sellThrough.slow.map((item) => (
                  <SellRow
                    key={item.liveListing.id}
                    title={item.product.name || item.product.productType}
                    sub={`${item.product.productType} · listed on ${item.liveListing.venue}`}
                    pillText={`${Math.round(item.daysSinceListed)}d`}
                    pillClass="bg-rose-500/20 text-rose-500"
                  />
                ))
              )}
            </SellSubsection>
          </Section>
        )}

        {filteredSales.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-10 text-center">
            <BarChart2 className="h-8 w-8 text-muted-foreground" />
            <div className="text-base font-semibold text-foreground">
              No sales in this period yet
            </div>
            <div className="text-xs text-muted-foreground">
              Log your first sale to see analytics.
            </div>
          </div>
        ) : (
          <>
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Stat
                label="Net Profit"
                value={`$${stats.totalNet.toFixed(2)}`}
                sub={`${stats.roi.toFixed(1)}% ROI`}
                accent
              />
              <Stat
                label="Total Revenue"
                value={`$${stats.totalRevenue.toFixed(2)}`}
                sub={`${filteredSales.length} sales`}
              />
              <Stat
                label="Profit Margin"
                value={`${stats.margin.toFixed(1)}%`}
                sub="net / gross"
              />
              <Stat
                label="Avg Sale"
                value={`$${stats.avgSale.toFixed(2)}`}
                sub="per transaction"
              />
              <Stat
                label="Fees Paid"
                value={`$${stats.totalFees.toFixed(2)}`}
                sub="platform + payment"
              />
              <Stat
                label="Shipping"
                value={`$${stats.totalShipping.toFixed(2)}`}
                sub="actual cost"
              />
            </div>

            {byVenue.length > 0 && (
              <Section title="Profit by Platform">
                {byVenue.map(([venue, data]) => (
                  <BarRow
                    key={venue}
                    label={venue}
                    value={data.profit}
                    max={maxVenueProfit}
                    color={VENUE_COLORS[venue as SaleVenue] ?? "#8e8e93"}
                    count={data.count}
                  />
                ))}
              </Section>
            )}

            {byLot.length > 0 && (
              <Section title="Profit by Lot">
                {byLot.map((lot) => {
                  const roi =
                    lot.invested > 0
                      ? ((lot.net / lot.invested) * 100).toFixed(0)
                      : "—";
                  return (
                    <div key={lot.name + lot.invested} className="space-y-1.5">
                      <div className="h-1.5 overflow-hidden rounded-full bg-border">
                        <div
                          className={`h-full rounded-full ${lot.net >= 0 ? "bg-secondary" : "bg-rose-500"}`}
                          style={{
                            width: `${Math.max(0, Math.min(100, (Math.abs(lot.net) / maxLotNet) * 100))}%`,
                          }}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                          {lot.name}
                        </div>
                        <div className="text-right">
                          <div
                            className={`text-sm font-bold ${lot.net >= 0 ? "text-secondary" : "text-rose-500"}`}
                          >
                            {lot.net >= 0 ? "+" : ""}${lot.net.toFixed(2)}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            ROI {roi}% · {lot.sales} sales
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Section>
            )}

            {bundlePerformance.length > 0 && (
              <Section title="Bundle Performance">
                {bundlePerformance.map((stat) => (
                  <div key={stat.strategyId} className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                        {stat.meta.name}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {stat.soldCount}/{stat.productCount} sold
                      </div>
                    </div>
                    <div className="flex items-stretch divide-x divide-border">
                      <BundleStat
                        label="Avg ROI"
                        value={
                          stat.avgRoi == null
                            ? "—"
                            : `${stat.avgRoi >= 0 ? "+" : ""}${stat.avgRoi.toFixed(0)}%`
                        }
                        valueClass={
                          stat.avgRoi == null
                            ? "text-muted-foreground"
                            : stat.avgRoi >= 0
                              ? "text-secondary"
                              : "text-rose-500"
                        }
                      />
                      <BundleStat
                        label="Days to Sell"
                        value={
                          stat.avgDaysToSell == null
                            ? "—"
                            : stat.avgDaysToSell.toFixed(1)
                        }
                      />
                      <BundleStat
                        label="Net"
                        value={
                          stat.soldCount === 0
                            ? "—"
                            : `${stat.totalNet >= 0 ? "+" : ""}$${stat.totalNet.toFixed(0)}`
                        }
                        valueClass={
                          stat.totalNet >= 0
                            ? "text-secondary"
                            : "text-rose-500"
                        }
                      />
                    </div>
                  </div>
                ))}
              </Section>
            )}

            <Section title="Summary">
              <div className="space-y-0">
                {[
                  [
                    "Total Invested (all lots)",
                    `$${stats.totalInvested.toFixed(2)}`,
                  ],
                  ["Total Revenue", `$${stats.totalRevenue.toFixed(2)}`],
                  ["Total Fees Paid", `$${stats.totalFees.toFixed(2)}`],
                  [
                    "Total Shipping Costs",
                    `$${stats.totalShipping.toFixed(2)}`,
                  ],
                  ["Net Profit", `$${stats.totalNet.toFixed(2)}`],
                  ["Overall ROI", `${stats.roi.toFixed(1)}%`],
                ].map(([label, value], i, arr) => (
                  <div
                    key={label}
                    className={`flex items-center justify-between py-2 ${
                      i < arr.length - 1 ? "border-b border-border" : ""
                    }`}
                  >
                    <div className="text-sm text-muted-foreground">{label}</div>
                    <div className="text-sm font-semibold text-foreground">
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}
      </div>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-md border border-border bg-card p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function SellSubsection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {children}
    </div>
  );
}

function SellRow({
  title,
  sub,
  pillText,
  pillClass,
}: {
  title: string;
  sub: string;
  pillText: string;
  pillClass: string;
}) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {title}
        </div>
        <div className="text-[11px] text-muted-foreground">{sub}</div>
      </div>
      <span
        className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold ${pillClass}`}
      >
        {pillText}
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-0.5 text-lg font-bold ${accent ? "text-secondary" : "text-foreground"}`}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function BarRow({
  label,
  value,
  max,
  color,
  count,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
  count: number;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-foreground">{label}</div>
      <div className="h-2 overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(0, Math.min(100, pct))}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">
          ${value.toFixed(2)}
        </div>
        <div className="text-[11px] text-muted-foreground">{count} sales</div>
      </div>
    </div>
  );
}

function BundleStat({
  label,
  value,
  valueClass = "text-foreground",
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5 px-2">
      <div className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-sm font-bold ${valueClass}`}>{value}</div>
    </div>
  );
}
